import { useEffect, useState, useCallback } from "react";
import { Table, Button, Modal, Form, Badge, Pagination } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faFileContract,
  faEdit,
  faDownload,
  faCheckCircle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import useAxiosPrivate from "../../../Pages/hooks/useAxiosPrivate";
import { baseURL, ACADIMAC } from "../../../API/API";
import { toast } from "react-toastify";
import Lodingsubmit from "../../Loding/Loding";

export default function AssignmentsDashboard() {
  const axiosPrivate = useAxiosPrivate();
  const [submissions, setSubmissions] = useState([]);
  const [academics, setAcademics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters state
  const [statusFilter, setStatusFilter] = useState("all");
  const [academicFilter, setAcademicFilter] = useState("all");

  // Grading Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [grade, setGrade] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submittingGrade, setSubmittingGrade] = useState(false);

  // Fetch student submissions
  const fetchSubmissions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        indexpage: page,
        status: statusFilter,
      };
      if (academicFilter !== "all") {
        params.academic_year_id = academicFilter;
      }
      
      const res = await axiosPrivate.post(`${baseURL}/learning/assignments-dashboard`, params);
      setSubmissions(res.data?.data || []);
      setTotalPages(res.data?.totalPages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching submissions:", err);
      toast.error("فشل تحميل تسليمات الواجبات");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, academicFilter, axiosPrivate]);

  // Fetch academic levels for filters
  useEffect(() => {
    async function fetchAcademics() {
      try {
        const res = await axiosPrivate.get(`${baseURL}/${ACADIMAC}`);
        setAcademics(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching academics:", err);
      }
    }
    fetchAcademics();
  }, [axiosPrivate]);

  // Refetch when filters change
  useEffect(() => {
    fetchSubmissions(1);
  }, [statusFilter, academicFilter, fetchSubmissions]);

  // Open grading modal
  const handleOpenGrading = (sub) => {
    setSelectedSub(sub);
    setGrade(sub.grade !== null ? sub.grade.toString() : "");
    setFeedback(sub.feedback || "");
    setShowModal(true);
  };

  // Submit Grade and Feedback
  const handleSaveGrade = async (e) => {
    e.preventDefault();
    if (!selectedSub) return;
    
    const numericGrade = parseFloat(grade);
    if (isNaN(numericGrade) || numericGrade < 0 || numericGrade > 10) {
      toast.error("يجب إدخال درجة صالحة بين 0 و 10");
      return;
    }

    setSubmittingGrade(true);
    try {
      await axiosPrivate.post(`${baseURL}/learning/grade-assignment`, {
        id: selectedSub.id,
        grade: numericGrade,
        feedback: feedback,
      });
      toast.success("تم تسجيل الدرجة والتقييم بنجاح");
      setShowModal(false);
      fetchSubmissions(currentPage);
    } catch (err) {
      console.error("Error saving grade:", err);
      const errorMsg = err?.response?.data?.message || "حدث خطأ أثناء حفظ التقييم";
      toast.error(errorMsg);
    } finally {
      setSubmittingGrade(false);
    }
  };

  return (
    <div className="p-4 content" dir="rtl">
      {loading && <Lodingsubmit />}
      
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h2 className="fw-bold m-0" style={{ color: "var(--brand-purple)" }}>
          لوحة تصحيح الواجبات الدراسية
        </h2>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border rounded-3 p-3 mb-4 shadow-sm text-end">
        <h5 className="fw-bold mb-3 small text-muted">تصفية النتائج:</h5>
        <Form className="row g-3">
          <Form.Group className="col-md-4">
            <Form.Label className="small">المرحلة الدراسية</Form.Label>
            <Form.Select
              value={academicFilter}
              onChange={(e) => setAcademicFilter(e.target.value)}
              className="custom-select"
            >
              <option value="all">كل المراحل</option>
              {academics.map((acd) => (
                <option key={acd.id} value={acd.id}>
                  {acd.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-md-4">
            <Form.Label className="small">حالة الواجب</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="custom-select"
            >
              <option value="all">كل التسليمات</option>
              <option value="pending">قيد المراجعة</option>
              <option value="graded">تم التصحيح والتقييم</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </div>

      {/* Submissions Table */}
      <div className="bg-white border rounded-3 shadow-sm overflow-hidden text-end">
        <Table responsive hover className="m-0 align-middle">
          <thead className="bg-light">
            <tr>
              <th className="py-3 text-secondary">اسم الطالب</th>
              <th className="py-3 text-secondary">المرحلة الدراسية</th>
              <th className="py-3 text-secondary">الدرس</th>
              <th className="py-3 text-secondary">تاريخ التسليم</th>
              <th className="py-3 text-secondary text-center">الملف</th>
              <th className="py-3 text-secondary text-center">الحالة</th>
              <th className="py-3 text-secondary text-center">الدرجة</th>
              <th className="py-3 text-secondary text-center">الإجراء</th>
            </tr>
          </thead>
          <tbody>
            {submissions.length > 0 ? (
              submissions.map((sub) => (
                <tr key={sub.id}>
                  <td className="fw-bold">
                    <div>{sub.student_name}</div>
                    <div className="small text-muted fw-normal">{sub.student_email}</div>
                  </td>
                  <td>
                    <span className="small">
                      <FontAwesomeIcon icon={faGraduationCap} className="me-1 text-primary" />
                      {sub.academic_year}
                    </span>
                    <div className="small text-muted">{sub.chapter_name}</div>
                  </td>
                  <td>
                    <span className="small">{sub.lesson_name}</span>
                  </td>
                  <td className="small text-muted">{sub.created_at}</td>
                  <td className="text-center">
                    <a
                      href={sub.file_path}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-primary rounded-pill px-3"
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-1" />
                      عرض
                    </a>
                  </td>
                  <td className="text-center">
                    {sub.status === "graded" ? (
                      <Badge bg="success" className="px-3 py-2 rounded-pill small">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        مصحح
                      </Badge>
                    ) : (
                      <Badge bg="warning" className="px-3 py-2 rounded-pill small text-dark">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        قيد المراجعة
                      </Badge>
                    )}
                  </td>
                  <td className="text-center fw-bold fs-6">
                    {sub.grade !== null ? (
                      <span className="text-success">{sub.grade} / 10</span>
                    ) : (
                      <span className="text-muted small">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    <Button
                      variant={sub.status === "graded" ? "outline-dark" : "dark"}
                      size="sm"
                      onClick={() => handleOpenGrading(sub)}
                      className="rounded-pill px-3"
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      {sub.status === "graded" ? "تعديل التقييم" : "رصد الدرجة"}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-5 text-muted">
                  لا توجد تسليمات واجبات مطابقة للبحث حالياً.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4" dir="ltr">
          <Pagination>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={idx + 1 === currentPage}
                onClick={() => fetchSubmissions(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </div>
      )}

      {/* Grading / Feedback Modal */}
      {selectedSub && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered dir="rtl" className="text-end">
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold">
              تقييم واجب الطالب: {selectedSub.student_name}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSaveGrade}>
            <Modal.Body>
              <div className="mb-3 p-3 bg-light rounded-3">
                <div className="mb-2"><strong>الدرس:</strong> {selectedSub.lesson_name}</div>
                <div className="mb-2"><strong>الوحدة / الشهر:</strong> {selectedSub.chapter_name}</div>
                <div>
                  <strong>رابط ملف إجابة الطالب: </strong>
                  <a
                    href={selectedSub.file_path}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary text-decoration-underline"
                  >
                    فتح وعرض الملف المرفوع
                  </a>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">درجة الواجب (من 10):</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  required
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  placeholder="ادخل درجة الطالب مثلاً: 9.5"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">ملاحظات / تعليقات المعلم للأخطاء:</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="اكتب ملاحظاتك للطالب لكي يتعلم من أخطائه ويصحح مساره..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-start">
              <Button variant="outline-danger" onClick={() => setShowModal(false)} disabled={submittingGrade}>
                إلغاء
              </Button>
              <Button variant="dark" type="submit" disabled={submittingGrade} className="px-4">
                {submittingGrade ? "جاري الحفظ..." : "حفظ التقييم"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </div>
  );
}
